import { useState } from "react";

import { cn } from "../../utils/cn";
import { Button } from "../button/Button";

import type { ComponentPropsWithoutRef, ReactNode } from "react";

export interface ShowMoreContainerProps<T>
  extends Omit<ComponentPropsWithoutRef<"div">, "children"> {
  items: T[];
  children: (item: T) => ReactNode;
  maxItems?: number;
  buttonVariant?: "link" | "neutralLink";
}

/**
 * A collapsible container that shows a constrained number of items, with a button to expand.
 */
export const ShowMoreContainer = <T,>({
  className,
  items,
  children,
  maxItems = 3,
  buttonVariant = "link",
  ...props
}: ShowMoreContainerProps<T>) => {
  const [expanded, setExpanded] = useState(false);

  const itemsToRender = expanded || items.length <= maxItems ? items : items.slice(0, maxItems - 1);

  return (
    <div {...props} className={cn("flex flex-col items-start", className)}>
      {itemsToRender.map(children)}
      {items.length > maxItems && (
        <Button
          variant={buttonVariant}
          className="h-auto p-0 leading-6"
          onClick={() => setExpanded(val => !val)}
        >
          {expanded ? "Show less" : `+${items.length - maxItems + 1} more`}
        </Button>
      )}
    </div>
  );
};
